import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { NIVEIS, DIMENSOES, nivelLabelColor, conceitoColor } from "@/lib/constants";
import {
  FileText, FileSpreadsheet, Loader2, Pencil, ArrowLeft,
  ThumbsUp, AlertTriangle, EyeOff, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

export default function AvaliacaoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const back = new URLSearchParams(location.search).get("back");
  const backSuffix = back ? `?back=${encodeURIComponent(back)}` : "";
  const { isAdmin } = useAuth();
  const [av, setAv] = useState(null);
  const [erro, setErro] = useState(false);
  const [ocorrencias, setOcorrencias] = useState([]);

  useEffect(() => {
    setErro(false);
    api.get(`/avaliacoes/${id}`).then((r) => setAv(r.data)).catch(() => setErro(true));
  }, [id]);

  useEffect(() => {
    if (av?.aluno_id && av?.turma_id) {
      api.get(`/pratica/ocorrencias?turma_id=${av.turma_id}&aluno_id=${av.aluno_id}`).then((r) => setOcorrencias(r.data)).catch(() => {});
    }
  }, [av?.aluno_id, av?.turma_id]);

  const exportar = async (fmt) => {
    try {
      await downloadFile(`/avaliacoes/${id}/export/${fmt}`, `MÉTRON_${(av.aluno_nome || "avaliacao").replace(/\s/g, "_")}.${fmt}`);
    } catch { toast.error("Falha ao exportar"); }
  };

  if (erro) return (
    <div className="text-center py-20">
      <p className="text-muted-foreground">Avaliação não encontrada ou indisponível.</p>
      <button data-testid="voltar-btn" onClick={() => navigate("/avaliacoes")} className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
        <ArrowLeft className="h-4 w-4" /> Voltar para Avaliações
      </button>
    </div>
  );

  if (!av) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const res = av.resultado;
  const concluida = av.status === "concluida";
  const isPI = av.banco_contexto === "primeiro_interventor";
  const notaFinal10 = res && res.media_geral != null ? (res.media_geral * 2).toFixed(2).replace(".", ",") : "—";

  return (
    <div>
      <button data-testid="voltar-btn" onClick={() => navigate(back || "/avaliacoes")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> {back ? "Voltar" : "Avaliações"}
      </button>

      <PageHeader title={av.aluno_nome} subtitle={`${av.curso_nome} · ${av.turma_nome} · ${av.oficina_nome}${av.tipologia_cec_nome ? ` · CEC: ${av.tipologia_cec_nome}` : ""}`}>
        <div className="flex flex-wrap gap-2">
          {!concluida && (
            <Button data-testid="continuar-btn" onClick={() => navigate(`/avaliar/${id}${backSuffix}`)}>
              <Pencil className="h-4 w-4 mr-2" /> Continuar avaliação
            </Button>
          )}
          {concluida && isAdmin && (
            <Button variant="secondary" data-testid="editar-btn" onClick={() => navigate(`/avaliar/${id}${backSuffix}`)}>
              <Pencil className="h-4 w-4 mr-2" /> Editar
            </Button>
          )}
          <Button variant="secondary" data-testid="export-docx-btn" onClick={() => exportar("docx")}>
            <FileText className="h-4 w-4 mr-2" /> Word
          </Button>
          <Button variant="secondary" data-testid="export-xlsx-btn" onClick={() => exportar("xlsx")}>
            <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel
          </Button>
        </div>
      </PageHeader>

      {(av.aluno_grau || av.aluno_instituicao || av.aluno_pais) && (
        <div className="flex flex-wrap gap-1.5 mb-6" data-testid="aluno-info-badges">
          {av.aluno_grau && <Badge className="bg-secondary text-secondary-foreground border-border">{av.aluno_grau}</Badge>}
          {av.aluno_instituicao && <Badge className="bg-primary/15 text-primary border-primary/30">{av.aluno_instituicao}</Badge>}
          {av.aluno_pais && <Badge className="bg-secondary text-secondary-foreground border-border">{av.aluno_pais}</Badge>}
        </div>
      )}

      {!concluida && (
        <Card className="p-4 mb-6 bg-orange-500/10 border-orange-500/30">
          <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/40">Em andamento</Badge>
          <span className="text-sm text-muted-foreground ml-3">Esta avaliação ainda não foi finalizada. O resultado abaixo é parcial.</span>
        </Card>
      )}

      {res && res.sem_criterios && (
        <Card className="p-6 bg-card border-border text-center" data-testid="sem-criterios-card">
          <EyeOff className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">{res.mensagem || "Avaliação sem critérios observados suficientes para cálculo."}</p>
        </Card>
      )}

      {res && !res.sem_criterios && (
        <>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground font-mono mb-4" data-testid="resultado-resumo">
            <span>{res.total_criterios} selecionados</span>
            <span>· {res.criterios_avaliados} avaliados</span>
            <span>· {res.nao_observados_count ?? (res.nao_observados || []).length} N/O</span>
            <span>· soma {res.soma}</span>
            <span>· média {res.media_geral != null ? res.media_geral.toFixed(2) : "—"}</span>
          </div>
          {(av.historico_contexto || av.dinamica) && (
            <Card className="p-5 bg-card border-border mb-6" data-testid="contexto-card">
              <h3 className="font-heading text-lg font-bold uppercase tracking-tight mb-2">Contexto do Cenário e Dinâmica</h3>
              {av.historico_contexto && <p className="text-sm text-muted-foreground mb-1"><span className="text-foreground font-medium">Contexto do Cenário:</span> {av.historico_contexto}</p>}
              {av.dinamica && <p className="text-sm text-muted-foreground"><span className="text-foreground font-medium">Dinâmica:</span> {av.dinamica}</p>}
            </Card>
          )}
          {/* Resumo geral */}
          <div className={`grid gap-4 mb-6 ${isPI ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"}`}>
            <Card className="p-5 bg-card border-border sm:col-span-1 flex flex-col justify-center items-center">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">Competência</div>
              <Badge className={`${(av.escala_labels ? conceitoColor(res.nivel_geral?.label) : nivelLabelColor(res.nivel_geral?.label))} mt-2 text-base px-3 py-1`}>{res.nivel_geral?.label}</Badge>
              {res.situacao && (
                <Badge data-testid="situacao-badge" className={`mt-2 text-sm px-3 py-1 ${res.aprovado ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-red-500/20 text-red-300 border-red-500/40"}`}>{res.situacao}</Badge>
              )}
            </Card>
            <Card className="p-5 bg-card border-border">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Pontuação média</div>
              <div className="font-heading text-4xl font-extrabold mt-1">{res.media_geral ?? "—"}<span className="text-lg text-muted-foreground">/5</span></div>
            </Card>
            {isPI && (
              <Card className="p-5 bg-card border-border" data-testid="nota-final-card">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Nota final</div>
                <div className="font-heading text-4xl font-extrabold mt-1 text-primary" data-testid="nota-final-valor">{notaFinal10}<span className="text-lg text-muted-foreground">/10</span></div>
                <div className="text-xs text-muted-foreground mt-1">Escala 0–10 (média × 2)</div>
              </Card>
            )}
            <Card className="p-5 bg-card border-border">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Desempenho</div>
              <div className="font-heading text-4xl font-extrabold mt-1">{res.percentual ?? "—"}<span className="text-lg text-muted-foreground">%</span></div>
              <div className="text-xs text-muted-foreground mt-1">{res.criterios_avaliados}/{res.total_criterios} critérios</div>
            </Card>
          </div>

          {/* Dimensões */}
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight mb-3">Por dimensão</h2>
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            {Object.entries(DIMENSOES).map(([k, d]) => {
              const dim = res.dimensoes[k];
              return (
                <Card key={k} data-testid={`dim-${k}`} className="p-5 bg-card border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <d.icon className="h-5 w-5" style={{ color: d.color }} />
                    <span className="font-semibold">{d.label}</span>
                  </div>
                  <div className="font-heading text-3xl font-extrabold">{dim.media ?? "—"}<span className="text-sm text-muted-foreground">/5</span></div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden mt-2">
                    <div className="h-full transition-all" style={{ width: `${dim.percentual || 0}%`, backgroundColor: d.color }} />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{dim.percentual != null ? `${dim.percentual}%` : "Não avaliado"} · {dim.avaliados} critérios</div>
                </Card>
              );
            })}
          </div>

          {/* Pontos fortes / a melhorar */}
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <Card className="p-5 bg-card border-border">
              <div className="flex items-center gap-2 mb-3"><ThumbsUp className="h-5 w-5 text-emerald-400" /><span className="font-semibold">Pontos fortes</span></div>
              <div className="flex flex-wrap gap-1.5">
                {res.pontos_fortes?.length ? res.pontos_fortes.map((p) => <Badge key={p} className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40">{p}</Badge>) : <span className="text-sm text-muted-foreground">—</span>}
              </div>
            </Card>
            <Card className="p-5 bg-card border-border">
              <div className="flex items-center gap-2 mb-3"><AlertTriangle className="h-5 w-5 text-orange-400" /><span className="font-semibold">Pontos a melhorar</span></div>
              <div className="flex flex-wrap gap-1.5">
                {res.pontos_melhorar?.length ? res.pontos_melhorar.map((p) => <Badge key={p} className="bg-orange-500/20 text-orange-300 border-orange-500/40">{p}</Badge>) : <span className="text-sm text-muted-foreground">—</span>}
              </div>
            </Card>
          </div>

          {/* Alertas de critérios críticos (não reprovam; ponto de atenção) */}
          {!!(res.criticos_alertas || []).length && (
            <Card className="p-5 bg-amber-500/10 border-amber-500/30 mb-6" data-testid="criticos-card">
              <div className="flex items-center gap-2 mb-3">
                <ShieldAlert className="h-5 w-5 text-amber-400" />
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight">Alertas de critérios críticos</h3>
              </div>
              <div className="space-y-2">
                {res.criticos_alertas.map((a, i) => (
                  <div key={i} data-testid={`critico-alerta-${i}`} className="text-sm">
                    <span className="font-medium">{a.nome}</span>
                    <Badge className="ml-2 bg-amber-500/20 text-amber-300 border-amber-500/40">nota {a.nivel}</Badge>
                    <p className="text-xs text-muted-foreground mt-0.5">{a.mensagem}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3">Critério crítico com nota baixa NÃO reprova automaticamente — a aptidão é determinada pela média geral.</p>
            </Card>
          )}

          {/* Detalhe critérios */}
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight mb-3">Critérios avaliados</h2>
          <Card className="bg-card border-border divide-y divide-border mb-6">
            {res.detalhes.map((d) => {
              const n = NIVEIS.find((x) => x.value === d.nivel);
              return (
                <div key={d.criterio_id} className="flex items-center justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{d.nome}</div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Badge className={`${DIMENSOES[d.dimensao]?.badge} text-[10px]`}>{DIMENSOES[d.dimensao]?.label} · peso {d.peso}</Badge>
                      {d.critico && <Badge className="bg-red-500/15 text-red-300 border-red-500/40 text-[10px]">Crítico</Badge>}
                    </div>
                  </div>
                  {d.nivel != null
                    ? <Badge className={n.badge}>{d.nivel} · {d.nivel_label}</Badge>
                    : <span className="flex items-center gap-1 text-xs text-muted-foreground"><EyeOff className="h-3.5 w-3.5" />Não observado</span>}
                </div>
              );
            })}
          </Card>

          {ocorrencias.length > 0 && (
            <Card className="p-5 bg-card border-border mb-6" data-testid="penalidades-card">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-5 w-5 text-orange-400" />
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight">Ocorrências / Penalidades</h3>
              </div>
              <div className="space-y-2">
                {ocorrencias.map((o) => (
                  <div key={o.id} className="flex items-center justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <span className="font-medium">{o.comportamento}</span>
                      {o.descricao && <span className="text-muted-foreground"> — {o.descricao}</span>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Badge className="bg-orange-500/15 text-orange-300 border-orange-500/30">−{o.status === "validada" ? o.valor_final : o.valor_sugerido} pts</Badge>
                      <Badge className={o.status === "validada" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : o.status === "rejeitada" ? "bg-red-500/20 text-red-300 border-red-500/40" : "bg-orange-500/20 text-orange-300 border-orange-500/40"}>
                        {o.status === "validada" ? "Validada" : o.status === "rejeitada" ? "Rejeitada" : "Pendente"}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3">As penalidades não descontam a média automaticamente (aguardando definição da regra oficial).</p>
            </Card>
          )}

          {(av.observacoes || av.pontos_fortes || av.pontos_melhorar) && (
            <Card className="p-5 bg-card border-border">
              <h3 className="font-heading text-lg font-bold uppercase tracking-tight mb-2">Observações do instrutor</h3>
              {av.observacoes && <p className="text-sm text-muted-foreground mb-2">{av.observacoes}</p>}
              {av.pontos_fortes && <p className="text-sm"><span className="text-emerald-400">Ponto positivo:</span> {av.pontos_fortes}</p>}
              {av.pontos_melhorar && <p className="text-sm"><span className="text-orange-400">Ocorrência/erro:</span> {av.pontos_melhorar}</p>}
              <p className="text-xs text-muted-foreground mt-3 font-mono">Avaliador: {av.avaliador_nome}</p>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
